import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ChevronLeft, MapPin, Calendar, Wallet, ShieldCheck, MessageCircle } from 'lucide-react'
import { Page, Card, btnPrimary, btnSecondary, SkeletonRows } from '@/components/rm'
import { Field, Input, Textarea } from '@/components/rm/form'
import { useAuth } from '@/hooks/use-auth'
import { fmtDate, fmtDateTime, phDay, pesoFromCentavos } from '@/lib/ph'
import { useCategories } from '@/lib/listing'
import { useTaskForCompany, useTaskPhotos } from '@/lib/tasks'
import { OFFER_LABEL } from './marketplace'
import { errorText } from '@/lib/utils'
import { startAsBusiness } from '@/lib/messages'

// Spec 005 US2. One task as a business sees it, and its offer: make, change, withdraw.
export const Route = createFileRoute('/_app/marketplace_/$id')({ component: TaskForBusiness })

function TaskForBusiness() {
  const { id } = Route.useParams()
  const { role } = useAuth()
  const canOffer = role === 'company-admin'
  const { task: q, saveOffer } = useTaskForCompany(id)
  const navigate = useNavigate()
  const cats = useCategories()
  const t = q.data
  const photos = useTaskPhotos(null, id, t?.photo_count ?? 0)
  const [price, setPrice] = useState('')
  const [message, setMessage] = useState('')
  useEffect(() => {
    if (t?.my_offer) { setPrice(String(t.my_offer.price_centavos / 100)); setMessage(t.my_offer.message) }
  }, [t?.my_offer])

  if (q.isLoading) return <Page><Card className="mt-5"><SkeletonRows /></Card></Page>
  if (!t) return <Page><p className="mt-5 text-[14px] text-token-secondary">This task is not available to your business.</p></Page>

  const open = t.status === 'open'
  const editable = open && canOffer && (!t.my_offer || ['active', 'withdrawn'].includes(t.my_offer.status))
  const save = async (status?: 'active' | 'withdrawn') => {
    const p = Number(price)
    if (!(p > 0)) return toast.error('Enter your price in pesos.')
    try {
      await saveOffer.mutateAsync({ id: t.my_offer?.id, price_pesos: p, message, status })
      toast.success(status === 'withdrawn' ? 'Offer withdrawn' : t.my_offer ? 'Offer updated' : 'Offer sent')
    } catch (e) { toast.error(errorText(e, 'Could not save the offer')) }
  }

  return (
    <Page>
      <Link to="/marketplace" className="mt-1 inline-flex items-center gap-1 text-[13px] font-semibold text-token-secondary"><ChevronLeft className="h-4 w-4" />Tasks near you</Link>
      <Card className="mt-3 space-y-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h1 className="font-display text-[20px] font-bold leading-tight text-token-primary">{t.title}</h1>
          {t.my_offer && <span className="flex-none rounded-full bg-token-hover px-2.5 py-0.5 text-[12px] font-semibold text-token-secondary">{OFFER_LABEL[t.my_offer.status]}</span>}
        </div>
        <p className="text-[12.5px] text-token-secondary">{cats.data?.find((c) => c.slug === t.category_slug)?.name_en} · {t.client_first_name} · posted {fmtDateTime(t.created_at)} · {t.offer_count} offer{t.offer_count === 1 ? '' : 's'}</p>
        {t.description && <p className="whitespace-pre-line text-[14.5px] text-token-primary">{t.description}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-token-secondary">
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{[t.barangay, t.city].filter(Boolean).join(', ')}</span>
          {t.budget_centavos != null && <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />Budget {pesoFromCentavos(t.budget_centavos)}</span>}
          {t.preferred_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(phDay(t.preferred_date))}</span>}
        </div>
        {(photos.data ?? []).length > 0 && <div className="flex gap-2 overflow-x-auto">{photos.data!.map((u) => <img key={u} src={u} alt="" className="h-28 w-28 flex-none rounded-[10px] object-cover" />)}</div>}
        {!open && <p className="text-[13px] font-semibold text-token-muted">This task is {t.status}.</p>}
        <button type="button" className={btnSecondary} onClick={async () => {
          try { const th = await startAsBusiness(t.id); navigate({ to: '/inbox/$id', params: { id: th } }) } catch (e) { toast.error(errorText(e, 'Could not open the conversation')) }
        }}><MessageCircle className="h-4 w-4" /> Message the client</button>
      </Card>

      {(editable || t.my_offer) && (
        <Card className="mt-5 p-5">
          <p className="mb-3 text-[15px] font-bold text-token-primary">{t.my_offer ? 'Your offer' : 'Make an offer'}</p>
          <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
            <Field label="Price in pesos"><Input type="number" min={1} step={50} value={price} disabled={!editable} onChange={(e) => setPrice(e.target.value)} placeholder="1300" /></Field>
            <Field label="Message to the client"><Textarea rows={3} maxLength={1000} value={message} disabled={!editable} onChange={(e) => setMessage(e.target.value)} placeholder="When you can come, what the price includes, and what may cost extra." /></Field>
          </div>
          <p className="mt-3 flex items-start gap-2 text-[12.5px] text-token-secondary"><ShieldCheck className="mt-0.5 h-4 w-4 flex-none" />Phone numbers, emails and links are hidden from the client until the booking is paid.</p>
          {editable && (
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              {t.my_offer?.status === 'active' && <button className={btnSecondary} disabled={saveOffer.isPending} onClick={() => save('withdrawn')}>Withdraw</button>}
              <button className={btnPrimary} disabled={saveOffer.isPending} onClick={() => save('active')}>
                {!t.my_offer ? 'Send offer' : t.my_offer.status === 'withdrawn' ? 'Send again' : 'Update offer'}
              </button>
            </div>
          )}
          {t.my_offer?.status === 'accepted' && <p className="mt-3 text-[13px] font-semibold text-token-success">The client accepted your offer. Booking and payment come next.</p>}
        </Card>
      )}
    </Page>
  )
}
